import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  flip,
  offset,
  shift,
  useRole,
  useDismiss,
  useInteractions,
  useListNavigation,
  FloatingPortal,
  FloatingFocusManager,
  FloatingOverlay,
} from "@floating-ui/react";
import cx from "classnames";

import "./ContextMenu.scss";
import { Card } from "../Card";
import { Flex } from "../Flex";

const renderContent = (label: string | React.ReactNode) => {
  return React.isValidElement(label) ? label : label;
};

type ContextMenuItemButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string | React.ReactNode;
    disabled?: boolean;
  };

const ContextMenuItemButton = forwardRef<
  HTMLButtonElement,
  ContextMenuItemButtonProps
>(({ label, disabled, ...props }, ref) => {
  const appliedClasses = cx("ContextMenuItem");
  return (
    <button
      {...props}
      className={appliedClasses}
      ref={ref}
      role="menuitem"
      disabled={disabled}
    >
      {renderContent(label)}
    </button>
  );
});

type ContextMenuItemLinkProps =
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    label: string | React.ReactNode;
  };

const ContextMenuItemLink = forwardRef<
  HTMLAnchorElement,
  ContextMenuItemLinkProps
>(({ label, ...props }, ref) => {
  const appliedClasses = cx("ContextMenuItem");
  return (
    <a {...props} className={appliedClasses} ref={ref} role="menuitem">
      {renderContent(label)}
    </a>
  );
});

type ContextMenuItemSectionTitleProps = {
  label: string | React.ReactNode;
};

const ContextMenuItemSectionTitle = ({
  label,
}: ContextMenuItemSectionTitleProps) => {
  return (
    <div className="ContextMenuItemSectionTitle">{renderContent(label)}</div>
  );
};

type ContextMenuTextItemProps = {
  label: string | React.ReactNode;
};

const ContextMenuTextItem = ({ label }: ContextMenuTextItemProps) => {
  return <div className="ContextMenuTextItem">{renderContent(label)}</div>;
};

type ContextMenuItem = {
  label: string | React.ReactNode;
  isSectionTitle?: boolean;
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

type ContextMenuProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  location: { x: number; y: number };
  setLocation?: (location: { x: number; y: number }) => void;
  items: ContextMenuItem[];
  className?: string;
  useWhiteBackground?: boolean;
  placeholder?: string;
};

export const ContextMenu = forwardRef<
  HTMLButtonElement,
  ContextMenuProps & React.HTMLProps<HTMLButtonElement>
>(
  (
    {
      isOpen,
      setIsOpen,
      location,
      items,
      useWhiteBackground = false,
      className,
      placeholder,
    },
    _forwardedRef,
  ) => {
    // We keep a copy of this variable locally so we can have more control over
    // when the menu opens.
    const [localIsOpen, setLocalIsOpen] = useState(false);

    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const listItemsRef = useRef<Array<HTMLButtonElement | null>>([]);

    const { refs, floatingStyles, context } = useFloating({
      open: localIsOpen,
      onOpenChange: setIsOpen,
      middleware: [
        offset({ mainAxis: 5, alignmentAxis: 4 }),
        flip({
          fallbackPlacements: ["left-start"],
        }),
        shift({ padding: 10 }),
      ],
      placement: "right-start",
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
    });

    const role = useRole(context, { role: "menu" });
    const dismiss = useDismiss(context);
    const listNavigation = useListNavigation(context, {
      listRef: listItemsRef,
      onNavigate: setActiveIndex,
      activeIndex,
    });

    const { getFloatingProps, getItemProps } = useInteractions([
      role,
      dismiss,
      listNavigation,
    ]);

    useEffect(() => {
      refs.setPositionReference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: location.x,
            y: location.y,
            top: location.y,
            right: location.x,
            bottom: location.y,
            left: location.x,
          };
        },
      });

      setLocalIsOpen(isOpen);
    }, [refs, location, isOpen]);

    return (
      <FloatingPortal>
        {localIsOpen && (
          <FloatingOverlay lockScroll style={{ pointerEvents: "none" }}>
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}
            >
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, pointerEvents: "auto" }}
                {...getFloatingProps()}
                className={cx("ContextMenu", className)}
              >
                <Card className="ContextMenu__Card">
                  <Flex direction="column">
                    {items && items.length > 0 ? (
                      items.map((item, index) => {
                        if (!item) return null;

                        const floatingUiProps = getItemProps({
                          tabIndex: activeIndex === index ? 0 : -1,
                          ref(node: HTMLButtonElement | null) {
                            listItemsRef.current[index] = node;
                          },
                          onClick(e: React.MouseEvent) {
                            item.onClick?.(e as any);
                            setIsOpen(false);
                          },
                        });

                        const { onClick: _, ...itemWithoutOnClick } = item;
                        const allProps = { ...floatingUiProps, ...itemWithoutOnClick };

                        if (item.href) {
                          return (
                            <ContextMenuItemLink key={index} {...allProps} />
                          );
                        } else if (item.onClick) {
                          return (
                            <ContextMenuItemButton key={index} {...allProps} />
                          );
                        } else if (item.isSectionTitle) {
                          return (
                            <ContextMenuItemSectionTitle
                              key={index}
                              label={item.label}
                            />
                          );
                        } else {
                          return (
                            <ContextMenuTextItem key={index} {...allProps} />
                          );
                        }
                      })
                    ) : (
                      <p className="ContextMenu__Placeholder">
                        {placeholder || "No items found"}
                      </p>
                    )}
                  </Flex>
                </Card>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    );
  },
);
